/**
 * TRINH SOAN TAI CHO - GIO CHI CON O GO.
 *
 * O soan nam DE LEN dung khoi van ban, cung vi tri, xap xi cung co chu. Nguoi
 * dung khong bi nhay sang mot hop thoai o goc man hinh roi phai tu ghep lai xem
 * minh dang sua cai gi.
 *
 * BO DIEU KHIEN KIEU CHU (phong, co chu, dam / nghieng, can le) VA BANG PHAN HOI
 * (ket qua chay thu, canh bao thay font) da chuyen sang bang Thong tin o popup -
 * xem ui/PropertiesPanel.tsx va ui/EditFeedback.tsx. O day chi con o go va nut
 * Áp dụng / Hủy. Popup TU MO khi bat dau sua, nen cac dieu khien luon voi toi
 * duoc ma khong phai bam them.
 *
 * =============================================================================
 *  NUT ÁP DỤNG SANG KHI CO BAT KY THAY DOI NAO - KHONG CHI KHI DOI CHU
 * =============================================================================
 *  Truoc day nut chi sang khi `draft !== originalText`. Nen doi rieng phong / co
 *  chu / can le ma giu nguyen chu thi khong ap dung duoc - mot thao tac hop le bi
 *  khoa. Gio nut sang khi BAT KY truong nao khac ban dau: van ban, phong, co chu,
 *  dam, nghieng, hoac can le.
 *
 *  Nhung CUA SO TUOI MOI van nguyen ven: bao cao chi co gia tri cho DUNG bo sau
 *  (van ban, can le, dam, nghieng, phong, co chu) ma no duoc tinh. Mot thay doi
 *  kieu chu lam bao cao het han, va nut lai khoa cho den khi bao cao moi ve -
 *  khong bao gio duoc ap dung mot thay doi kieu chu dua tren bao cao cu.
 */
import type { CssRect } from '../viewer/geometry';
import type { ScopeKind } from '../core/edit/scope';
import { SCOPE_LABEL } from '../core/edit/scope';
import { TextEditorShell } from './TextEditorShell';
import { preflightIsFresh } from './textEditorRules';
import { type TextAlign, type TextReplaceReport } from './textEditCore';
import { DEFAULT_FONT_FAMILY_ID, getFontFamily, type FontFamilyId } from '../core/fonts/families';

/**
 * BE RONG CO DINH cua o soan tai cho, CSS pixel.
 *
 * Truoc day o soan rong bang chinh khoi chu (Math.max(80, rect.width)), nen mot
 * o hep ("2") cho ra o soan hep con o rong cho ra o soan rong - hang nut Áp dụng
 * / Hủy nhay kich thuoc lung tung moi lan bam sang o khac. Nguoi dung bao "cố
 * định chiều ngang cho hợp lý". Mot con so co dinh cho ca hang nut deu nhau va
 * du cho de go thoai mai; o van neo dung tai `left` cua o that.
 *
 * 400: du rong de ca hang duoi - dong goi y "Enter để lưu · Shift+Enter xuống
 * dòng · Esc hủy" cong hai nut Hủy / Áp dụng - nam gon TREN MOT HANG (do duoc
 * be rong tu nhien cua hang la 377px voi phong that cua app, con lai la du cho).
 */
const INLINE_EDITOR_WIDTH_PX = 400;

/**
 * Mot phien soan dang mo. App giu toan bo trang thai; thanh phan nay khong co
 * trang thai rieng - nho the ket qua chay thu, van ban nhap va lenh gui di khong
 * bao gio lech nhau.
 *
 * PHAM VI luon la mot run (o) duoi con tro: cau thang chon pham vi da bi go bo
 * theo yeu cau nguoi dung. `scopeKind` giu lai (luon 'run') chi de dat nhan.
 */
export interface EditSession {
  scopeId: string;
  parentBlockId: string;
  scopeKind: ScopeKind;
  scopeIndex: number;
  scopeSiblingCount: number;
  pageIndex: number;
  /** Van ban goc CUA PHAM VI, dung lam previousText khi dung lenh. */
  originalText: string;
  draft: string;
  report: TextReplaceReport | null;
  reportForText: string | null;
  reportForAlign: TextAlign | null;
  align: TextAlign | null;
  onAlignChange: (align: TextAlign) => void;
  alignSupported: boolean;
  bold: boolean;
  italic: boolean;
  onBoldChange: (next: boolean) => void;
  onItalicChange: (next: boolean) => void;
  /** PHONG CHU cho chu moi, mot MA ho font (vd 'noto-serif'). */
  fontFamily: string;
  onFontFamilyChange: (id: FontFamilyId) => void;
  /**
   * CO CHU nguoi dung chon, hoac null = giu co chu do duoc tu chu cu.
   *
   * GIOI HAN ADR-005: duong ghi de KHONG noi rong duoc khoi, nen mot co chu LON
   * hon cho co the bi thu nho lai de vua - bang phan hoi noi ro (FitNote). Giam
   * co chu thi chay tron ven.
   */
  fontSizePt: number | null;
  /** Co chu do duoc tu chu cu - gia tri hien khi chua chon gi (fontSizePt null). */
  baseFontSizePt: number;
  onFontSizeChange: (pt: number) => void;
  reportForBold: boolean;
  reportForItalic: boolean;
  reportForFamily: string | null;
  /** Co chu ma bao cao duoc tinh cho, null = giu co chu goc. */
  reportForSize: number | null;
  pending: boolean;
  unsupported: boolean;
  fontCount: number;
  onDraftChange: (text: string) => void;
  onCommit: () => void;
  onCancel: () => void;
  /** O go nhan tieu diem -> mo bang Thong tin. Xem TextEditorShell.onFocus. */
  onFocus?: (() => void) | undefined;
}

export interface InlineTextEditorProps {
  session: EditSession;
  /** Vi tri khoi trong he toa do DOM cua trang. */
  rect: CssRect;
  /** Co chu tren man hinh, CSS pixel (da nhan zoom). */
  fontSizePx: number;
  colorCss: string;
}

/**
 * Lan sua nay co KHAC ban dau khong. Nut Áp dụng sang khi bat ky truong nao doi:
 * van ban, phong, co chu, dam, nghieng, hoac can le. Trang thai "ban dau" khop
 * dung voi cach App dat lai moi lan mo pham vi (align null, khong dam/nghieng,
 * phong mac dinh, co chu null).
 */
export function sessionHasChange(session: EditSession): boolean {
  return (
    session.draft !== session.originalText ||
    session.align !== null ||
    session.bold ||
    session.italic ||
    session.fontFamily !== DEFAULT_FONT_FAMILY_ID ||
    session.fontSizePt !== null
  );
}

export function InlineTextEditor({ session, rect, fontSizePx, colorCss }: InlineTextEditorProps) {
  // Bao cao chi co gia tri khi no duoc tinh cho DUNG bo sau dang co. Chi dua vao
  // `pending` la chua du - xem ui/textEditorRules.ts.
  const reportIsFresh =
    session.reportForText !== null &&
    preflightIsFresh(
      {
        text: session.reportForText,
        align: session.reportForAlign,
        bold: session.reportForBold,
        italic: session.reportForItalic,
        family: session.reportForFamily,
        size: session.reportForSize,
      },
      {
        text: session.draft,
        align: session.align,
        bold: session.bold,
        italic: session.italic,
        family: session.fontFamily,
        size: session.fontSizePt,
      },
    );
  const blocked = reportIsFresh && session.report !== null && !session.report.ok;
  // Sang khi CO thay doi (bat ky truong nao) VA bao cao moi cho thay lan sua do
  // ghi ra duoc. Mot thay doi chi ve kieu chu, giu nguyen van ban, van ap dung duoc.
  const canCommit =
    !session.pending &&
    reportIsFresh &&
    session.report !== null &&
    session.report.ok &&
    sessionHasChange(session);

  // O go hien dung co chu se ghi ra: khi nguoi dung chon co chu khac, scale o
  // theo ty le so voi co chu goc de o khong lech han voi ket qua.
  const sizeRatio =
    session.fontSizePt !== null && session.baseFontSizePt > 0
      ? session.fontSizePt / session.baseFontSizePt
      : 1;

  return (
    <TextEditorShell
      variant="inline"
      left={rect.left}
      top={rect.top}
      width={INLINE_EDITOR_WIDTH_PX}
      // Doi pham vi = doi noi dung o soan, nen con tro phai dat lai.
      focusKey={session.scopeId}
      draft={session.draft}
      onDraftChange={session.onDraftChange}
      ariaLabel={`Sửa nội dung — ${SCOPE_LABEL[session.scopeKind].toLowerCase()}`}
      fontSizePx={fontSizePx * sizeRatio}
      colorCss={colorCss}
      minHeightPx={Math.max(fontSizePx * 1.35, rect.height)}
      blocked={blocked}
      bold={session.bold}
      italic={session.italic}
      // Doi phong o popup phai HIEN THAY ngay tren o soan - neu khong nguoi dung
      // tuong "chọn font không có tác dụng" du duong xuat da dung phong moi.
      fontFamilyCss={getFontFamily(session.fontFamily).cssStack}
      align={session.align ?? session.report?.alignDetected ?? null}
      onAlignChange={session.onAlignChange}
      alignSupported={session.alignSupported}
      canCommit={canCommit}
      commitLabel={session.pending ? 'Đang kiểm…' : 'Áp dụng'}
      commitTitle={
        session.pending
          ? 'Đang kiểm tra lần sửa này…'
          : canCommit
            ? 'Áp dụng thay đổi'
            : 'Chưa áp dụng được — xem lý do trong bảng Thông tin'
      }
      onCommit={session.onCommit}
      onCancel={session.onCancel}
      hint="Enter để lưu · Shift+Enter xuống dòng · Esc hủy"
      {...(session.onFocus ? { onFocus: session.onFocus } : {})}
    />
  );
}
